import { Stack, styled, Theme } from '@mui/material';
import { isBrowser } from 'react-device-detect';
import { useLocation } from 'react-router-dom';

import { Header } from '../../components/layout/Header/Header';
import { Footer } from '../../components/layout/Footer/Footer';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen';
import { usePinnedToVisualViewport } from '../../hooks/usePinnedToVisualViewport';
import { ROUTES } from '../../constants/routes';

// Fixed header height the <main> reserves via its top margin.
const HEADER_HEIGHT = 61;

import CabinetBgLogoIcon from '../../assets/icons/cabinetBgLogoIcon.svg?react';

const FULL_BLEED_ROUTES: string[] = [`/${ROUTES.CABINET}/${ROUTES.MAP}`];

const StyledMain = styled('main')<{ keyboardOpen?: boolean }>(
  ({ keyboardOpen }) => ({
    flex: '1 1 auto',
    margin: '61px 0 0',
    paddingBottom: keyboardOpen ? 0 : '57px',
    backgroundColor: '#353434',
  }),
);

export const CabinetLayout = ({ children }: { children: React.ReactNode }) => {
  const isKeyboardOpen = useKeyboardOpen();
  // Bounding the column to the visible area is only half of it. iOS overlays
  // the keyboard and scrolls the still-full-height document to reveal the
  // focused field, so the column ends up scrolled off its own top: the field
  // being typed into sat clipped under the header with a tall empty gap above
  // the keyboard. Pinned while the keyboard is open, and only then — cabinet
  // pages scroll normally the rest of the time.
  const viewportHeight = usePinnedToVisualViewport(isKeyboardOpen);
  const { pathname } = useLocation();
  const isFullBleed = FULL_BLEED_ROUTES.includes(pathname);

  return (
    <>
      <Header fullBleed={isFullBleed} />
      <StyledMain keyboardOpen={isKeyboardOpen}>
        <Stack
          sx={(theme: Theme) => ({
            position: 'relative',
            maxWidth: isFullBleed ? '100%' : !isBrowser ? '100%' : '400px',
            width: '100%',
            minHeight: isKeyboardOpen
              ? 'auto'
              : 'calc(100dvh - 61px - 57px)',
            backgroundColor: theme.palette.gray[300],
            alignItems: 'center',
            overflow: 'hidden',
            // With the keyboard open, bound the content to the visible area
            // (visual viewport minus the fixed header) and let it scroll, so a
            // focused field — or the Start button below it — can be reached
            // above the keyboard instead of being clipped by overflow:hidden.
            ...(isKeyboardOpen && viewportHeight
              ? {
                  height: `${viewportHeight - HEADER_HEIGHT}px`,
                  overflowY: 'auto',
                }
              : {}),
          })}
        >
          {!isFullBleed && (
            <CabinetBgLogoIcon
              width="100%"
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
              }}
            />
          )}
          <Stack sx={{ zIndex: 2, height: '100%', width: '100%' }}>
            {children}
          </Stack>
        </Stack>
      </StyledMain>
      <Footer />
    </>
  );
};
